import { useEffect } from 'react'
import { FormattedMessage } from 'react-intl'

import withFieldArray from '@/components/Form/FieldWrappers/withFieldArray'
import { fetchAttributes } from '@/pages/OrganizationUserAttributesPage/actions'
import { useAppDispatch, useAppSelector } from '@/store/hooks'

import Audience from './Audience'
import DeleteAudience from './DeleteAudience'
import messages from './messages'
import RuleText from './RuleText'
import { newAudience } from './utils'

import './index.scss'

import { PlusIcon } from '@heroicons/react/20/solid'
import { UserGroupIcon, UserPlusIcon } from '@heroicons/react/24/outline'
import { has } from 'lodash'
import { v4 } from 'uuid'

import { getCurrentUser } from '@/store/selectors'

const AudienceTargeting = ({
  name = 'audiences',
  helper,
  value: audiences,
  touched,
  errors,
  required,
  title,
  variables,
  rulePrefixText,
  firstFilterMandatory = false,
  readOnly = false
}: any) => {
  const dispatch = useAppDispatch()
  const currentUser = useAppSelector(getCurrentUser)

  useEffect(() => {
    dispatch(fetchAttributes())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div className="audience-targeting w-full">
      {audiences &&
        audiences.map((audience: any, audienceIndex: any) => {
          return (
            <div className="audience" key={audience.id}>
              <div className="row audience-header">
                <h3 className="text-sm font-bold uppercase">
                  <FormattedMessage {...messages.audienceRule} values={{ index: audienceIndex + 1 }} />
                  {required && <span className="ml-1 text-red-500">*</span>}
                </h3>
                {!readOnly && (
                  <DeleteAudience
                    onClick={() => {
                      helper.remove(audienceIndex)
                    }}
                  />
                )}
              </div>
              <Audience
                name={`${name}[${audienceIndex}]`}
                variables={variables}
                firstFilterMandatory={firstFilterMandatory}
                readOnly={readOnly}
              />
              <RuleText filters={audience.filters} title={title} rulePrefixText={rulePrefixText} />
            </div>
          )
        })}
      {!readOnly && (
        <div className="mt-4 mb-2 flex space-x-3">
          <button
            onClick={() => {
              helper.push({
                id: v4(),
                filters: [
                  {
                    attribute: {
                      displayName: 'Individual',
                      id: 'individual',
                      type: 'string',
                      scimAttributeName: 'individual',
                      urn: 'id'
                    },
                    displayValue: currentUser.displayName || currentUser.email,
                    id: v4(),
                    ruleTextDisplayValue: currentUser.displayName || currentUser.email,
                    operator: 'eq',
                    value: currentUser.id
                  }
                ]
              })
            }}
            type="button"
            className="inline-flex items-center gap-x-1.5 rounded-md bg-white px-3 py-2 text-sm text-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
          >
            <UserPlusIcon className="h-4 w-4" />
            Add Me
          </button>
          <button
            onClick={() => {
              helper.push({
                id: v4(),
                filters: [
                  {
                    attribute: {
                      id: v4(),
                      displayName: 'Active',
                      type: 'boolean',
                      urn: 'urn:ietf:params:scim:schemas:core:2.0:User:active',
                      scimAttributeName: 'active'
                    },
                    displayValue: 'true',
                    id: v4(),
                    operator: 'eq',
                    value: 'true'
                  }
                ]
              })
            }}
            type="button"
            className="inline-flex items-center gap-x-1.5 rounded-md bg-white px-3 py-2 text-sm text-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
          >
            <UserGroupIcon className="h-4 w-4" />
            Add Everyone
          </button>
          <button
            onClick={() => {
              helper.push(newAudience())
            }}
            type="button"
            className="inline-flex items-center gap-x-1.5 rounded-md bg-white px-3 py-2 text-sm text-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
          >
            <PlusIcon className="-ml-0.5 h-4 w-4" aria-hidden="true" />
            Add Audience
          </button>
        </div>
      )}

      {has(errors, name) && has(touched, name) && (
        <span className="field__error mt-0 mb-0 font-normal text-red-500">Audience is required</span>
      )}
    </div>
  )
}

export default withFieldArray(AudienceTargeting)
